import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PaymentModal from "../../Modals/PaymentModal";
import TapPayment from "../../payment/TapPayment";
import {
  MasterPaymentCard,
  VisaPaymentCard,
} from "../../icons/Icons";
import {
  useAdminGetPaymentMethodsQuery,
  useAdminRetryPaymentMutation,
} from "../../../services/organization/subscription";
import { useCurrency } from "../../../hooks/useCurrency";

/**
 * RetryPaymentModal - Dynamic modal for retrying a failed subscription payment.
 *
 */
const RetryPaymentModal = ({ subscription, isOpened, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation("subscription");
  const { convertAndFormat } = useCurrency();
  const tapRef = useRef(null);

  const { data: pmData } = useAdminGetPaymentMethodsQuery(undefined, { skip: !isOpened, refetchOnMountOrArgChange: true });
  const [retryPayment, { isLoading }] = useAdminRetryPaymentMutation();

  const paymentMethods = pmData?.data?.payment_methods || [];
  const defaultCard = paymentMethods.find((c) => c.is_default) || paymentMethods[0];
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (isOpened) {
      const defaultError = subscription?.last_failed_transaction?.failure_reason 
        ? subscription.last_failed_transaction.failure_reason.split(" - ")[1] || subscription.last_failed_transaction.failure_reason 
        : null;
      setErrorMessage(defaultError);
    }
  }, [isOpened, subscription]);

  const effectiveCardId = selectedCardId ?? defaultCard?.id;
  const isBusy = isLoading || isRedirecting;

  const handleClose = () => {
    setErrorMessage(null);
    setShowNewCardForm(false);
    onClose();
  };

  const handleRetry = async () => {
    if (isBusy) return;
    setErrorMessage(null);

    try {
      let body;

      if (showNewCardForm) {
        if (!tapRef.current) return;
        const cardToken = await tapRef.current.tokenize();
        setIsRedirecting(true);
        body = { card_token: cardToken };
      } else {
        if (!effectiveCardId) {
          setErrorMessage(t("retryPayment.noCardSelected", "Please select a payment card."));
          return;
        }
        body = { payment_method_id: effectiveCardId };
      }

      const response = await retryPayment(body).unwrap();
      const redirectUrl = response?.data?.redirect_url;

      if (redirectUrl) {
        setIsRedirecting(true);
        // New card: 3DS redirect
        window.location.href = redirectUrl;
      } else {
        // Existing card: immediate result
        onSuccess?.(response?.data?.status);
        handleClose();
      }
    } catch (error) {
      setIsRedirecting(false);
      const msg =
        error?.data?.message ||
        error?.message ||
        t("retryPayment.failed", "Payment failed. Please try a different card.");
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  const handleTokenizationError = () => {
    setErrorMessage(
      t("retryPayment.tokenizationFailed", "Could not process card details. Please check and try again.")
    );
  };

  const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case "visa":
        return <VisaPaymentCard width={40} height={14} color="#222357" />;
      case "mastercard":
        return <MasterPaymentCard width={36} height={24} color="#222357" />;
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        );
    }
  };

  // Use the pending order amount (computed by PriceCalculationService) as the source of truth.
  // Falls back to the subscription snapshot amount if no pending order exists yet.
  const pendingOrder = subscription?.pending_order;
  const rawAmount = pendingOrder?.total_amount ?? subscription?.total_amount;
  // Keep as plain float for the TapPayment SDK (it needs a numeric value)
  const chargeAmount = rawAmount != null ? parseFloat(rawAmount) : null;
  const chargeCurrency = pendingOrder?.currency ?? subscription?.currency ?? "KWD";

  // Use convertAndFormat for display — respects ISO 4217 decimal digits (KWD = 3)
  const amountLabel = chargeAmount != null
    ? convertAndFormat(chargeAmount)
    : "";

  return (
    <PaymentModal
      id="RetryPaymentModal"
      isOpened={isOpened}
      onClose={handleClose}
      title={t("retryPayment.title", "Retry payment")}
      subtitle={t(
        "retryPayment.subtitle",
        "Your last subscription payment was unsuccessful. Please retry to maintain access."
      )}
      primaryLabel={
        isBusy
          ? t("retryPayment.processing", "Processing...")
          : `${t("retryPayment.retryBtn", "Retry payment")}${amountLabel ? ` ${amountLabel}` : ""}`
      }
      onPrimary={handleRetry}
      isPrimaryLoading={isBusy}
      isPrimaryDisabled={!showNewCardForm && !effectiveCardId}
      cancelLabel={t("common:cancel", "Cancel")}
      onCancel={handleClose}
      maxWidth={520}
    >
      {/*  Error Banner  */}
      {errorMessage && (
        <div className="retry-modal__error-banner" role="alert">
          <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 19L11 0L22 19H0ZM3.45 17H18.55L11 4L3.45 17ZM11 16C11.2833 16 11.5208 15.9042 11.7125 15.7125C11.9042 15.5208 12 15.2833 12 15C12 14.7167 11.9042 14.4792 11.7125 14.2875C11.5208 14.0958 11.2833 14 11 14C10.7167 14 10.4792 14.0958 10.2875 14.2875C10.0958 14.4792 10 14.7167 10 15C10 15.2833 10.0958 15.5208 10.2875 15.7125C10.4792 15.9042 10.7167 16 11 16ZM10 13H12V8H10V13Z" fill="#BE1622"/>
          </svg>

          <p>{errorMessage}</p>
        </div>
      )}

      {/*  Subscription Details  */}
      {subscription && (
        <div className="retry-modal__subscription-details">
          <div className="retry-modal__details-header">
            <span className="retry-modal__details-label">
              {t("retryPayment.subscriptionDetails", "Subscription Details")}
            </span>
            <span className="retry-modal__details-cadence">{subscription.cadence}</span>
          </div>

          <div className="retry-modal__details-row">
            <span>{t("retryPayment.plan", "Plan")}</span>
            <strong>{subscription?.plan?.title || ""}</strong>
          </div>

          <div className="retry-modal__details-row">
            <span>{t("retryPayment.dueDate", "Due Date")}</span>
            <strong>
              {subscription.renewal_at || subscription.expires_at ? (
                new Date(subscription.renewal_at || subscription.expires_at).toLocaleDateString(
                  i18n.language === "ar" ? "ar-EG" : "en-US",
                  { year: "numeric", month: "short", day: "numeric" }
                )
              ) : (
                ""
              )}
            </strong>
          </div>

          <div className="retry-modal__details-row retry-modal__details-row--total">
            <strong>{t("retryPayment.amountDue", "Amount Due")}</strong>
            <strong className="retry-modal__amount">{amountLabel}</strong>
          </div>
        </div>
      )}

      {/*  Card Selector  */}
      {!showNewCardForm && (
        <div className="retry-modal__card-section">
          <p className="retry-modal__section-label">
            {t("retryPayment.selectCard", "Select Payment Card")}
          </p>

          <div className="retry-modal__card-list">
            {paymentMethods.map((card) => {
              const isFailed = subscription?.last_failed_transaction?.organization_payment_method_id === card.id;
              
              return (
              <label
                key={card.id}
                className={`retry-modal__card-item ${
                  effectiveCardId === card.id ? "retry-modal__card-item--selected" : ""
                }`}
              >
                <span className="retry-modal__card-icon">{getCardIcon(card.brand)}</span>
                <span className="retry-modal__card-info">
                  <span className="retry-modal__card-name">
                    {card.brand?.charAt(0).toUpperCase() + card.brand?.slice(1)} ****{card.last4}
                  </span>
                  {card.is_default && (
                    <span className="retry-modal__card-expiry">
                      {t("paymentMethods.defaultCard", "Default")}
                    </span>
                  )}
                  {isFailed && (
                    <span className="retry-modal__card-expiry" style={{ color: "#BE1622", fontWeight: 700, textTransform: "uppercase" }}>
                      {t("retryPayment.transactionFailed", "Transaction Failed")}
                    </span>
                  )}
                </span>
                <input
                  type="radio"
                  name="retry-card"
                  value={card.id}
                  checked={effectiveCardId === card.id}
                  onChange={() => setSelectedCardId(card.id)}
                  className="retry-modal__card-radio"
                  style={isFailed && effectiveCardId === card.id ? { accentColor: "#BE1622" } : {}}
                />
              </label>
            );})}
          </div>

          <button
            type="button"
            className="retry-modal__add-card-link"
            onClick={() => setShowNewCardForm(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t("retryPayment.addNewCard", "Add new card")}
          </button>
        </div>
      )}

      {/*  New Card Form  */}
      {showNewCardForm && (
        <div className="retry-modal__card-section">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <button
              type="button"
              className="retry-modal__add-card-link"
              style={{ fontSize: 13 }}
              onClick={() => { setShowNewCardForm(false); setErrorMessage(null); }}
            >
              ← {t("retryPayment.backToCards", "Back to saved cards")}
            </button>
          </div>
          <TapPayment
            ref={tapRef}
            hideHeader
            onTokenizationError={handleTokenizationError}
            onReady={() => setErrorMessage(null)}
            customer={{}}
            transaction={{ amount: chargeAmount || 1, currency: chargeCurrency }}
            interfaceConfig={{
              locale: i18n.language === "ar" ? "ar" : "en",
              direction: i18n.language === "ar" ? "rtl" : "ltr",
              theme: "light",
            }}
          />
        </div>
      )}

      {/* Security note */}
      {/* <p className="payment-modal__security-note payment-modal__security-note--centered">
        {t("retryPayment.securityNote", "Secure SSL Encryption • PCI Compliant")}
      </p> */}
    </PaymentModal>
  );
};

RetryPaymentModal.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.number,
    total_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currency: PropTypes.string,
    cadence: PropTypes.string,
    plan_name: PropTypes.string,
    subscription_plan: PropTypes.shape({ name: PropTypes.string }),
    pending_order: PropTypes.shape({
      id: PropTypes.number,
      total_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      currency: PropTypes.string,
    }),
  }),
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default RetryPaymentModal;
